/**
 * @fileoverview Custom React Query Hook for GET Requests
 * 
 * This module provides a custom hook that wraps React Query's useQuery
 * with enhanced functionality for GET requests in React applications. Features include:
 * 
 * - **useCustomQuery**: Enhanced GET request hook with custom error handling, caching, and retry logic
 * - **Integrated Error Handling**: Automatic error notifications via snackbar system
 * - **CSRF Protection**: Automatic CSRF token inclusion in requests
 * - **Response Caching**: Configurable cache policies for performance optimization
 * - **Retry Logic**: Intelligent retry mechanisms with exponential backoff
 * - **Component Safety**: Mount status checking to prevent memory leaks
 * - **Event Tracking**: Error event logging for monitoring and analytics
 * 
 * The hook integrates with the application's notification system, error tracking,
 * and provides enhanced developer experience with proper TypeScript support.
 * 
 * @version 1.0.0
 */

import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useCallback, useRef } from "react";
import { useGetIsMounted } from "rooks";

import { RETRY_DELAY } from "config/constants";
import { usePostEvent } from "hooks/usePostEvent/usePostEvent";
import { getCookie } from "utils/cookies";

import { fetchWithRefresh } from "./sessionRefresh/refresh";
import { ApiError, BaseQueryOptions, CachePolicy } from "./types";
import { getIsLoggingOut, handleResponse, handleRetry } from "./utils";

/**
 * Custom hook for GET requests with enhanced error handling and caching.
 * 
 * This hook wraps React Query's useQuery with additional functionality:
 * - Automatic error handling and user notifications
 * - Configurable cache policies for performance optimization
 * - Intelligent retry logic with exponential backoff
 * - CSRF token inclusion for security
 * - Component mount status checking to prevent memory leaks
 * - Error event tracking for monitoring
 * 
 * @template T - The expected type of the response data
 * @param queryKey - Unique identifier for the query used by React Query for caching
 * @param url - The API endpoint URL to fetch data from
 * @param options - Configuration options for the query behavior
 * 
 * @returns Enhanced query result object with wrapped refetch function and response access
 * 
 * @example
 * ```typescript
 * // Basic usage
 * const { data, error, isLoading } = useCustomQuery<User[]>(
 *   ["users"],
 *   "/api/users"
 * );
 * 
 * // With options
 * const { data, refetch } = useCustomQuery<UserProfile>(
 *   ["user", userId],
 *   `/api/users/${userId}`,
 *   {
 *     enabled: !!userId,
 *     cachePolicy: CachePolicy.CACHE,
 *     retry: 3,
 *     shouldShowErrorSnackbar: (response) => response.status !== 404
 *   }
 * );
 * 
 * // Using the wrapped refetch function
 * const handleRefresh = async () => {
 *   try {
 *     const freshData = await refetch();
 *     console.log("Fresh data:", freshData);
 *   } catch (error) {
 *     console.error("Refresh failed:", error);
 *   }
 * };
 * ```
 */
export const useCustomQuery = <T>(
  queryKey: QueryKey,
  url: string,
  options: BaseQueryOptions<T> = {}
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { sendErrorEvent } = usePostEvent();
  const getIsMounted = useGetIsMounted();
  const {
    cachePolicy = CachePolicy.NO_CACHE,
    retry = 0,
    enabled = true,
    refetchOnMount = true,
    ...queryOptions
  } = options;
  const responseRef = useRef<Response | undefined>();

  const queryResult = useQuery<T, ApiError>({
    queryKey,
    queryFn: async () => {
      if (getIsLoggingOut()) {
        throw new Error("Session expired — redirecting to login");
      }
      const response = await fetchWithRefresh(url, {
        headers: {
          "Cache-Control": cachePolicy,
          "Pragma": cachePolicy
        }
      });
      responseRef.current = response.clone();
      return handleResponse<T>(response, enqueueSnackbar);
    },
    enabled,
    refetchOnMount,
    retry: (failureCount: number, error: ApiError): boolean => (
      handleRetry(options, error, responseRef, enqueueSnackbar, getIsMounted, sendErrorEvent, failureCount, retry)
    ),
    retryDelay: (failureCount: number): number => RETRY_DELAY * (failureCount + 1),
    headers: { "X-CSRFToken": getCookie("csrftoken") || "" },
    ...queryOptions
  } as UseQueryOptions<T, ApiError>);

  const { refetch } = queryResult;

  // Create a wrapped refetch function that returns Promise<T> instead of the full query result
  const wrappedRefetch = useCallback(async (): Promise<T> => {
    const result = await refetch();
    if (!result.data) {
      throw result.error || new Error("Failed to fetch data");
    }
    return result.data;
  }, [refetch]);

  return {
    ...queryResult,
    refetch: wrappedRefetch,
    response: responseRef.current
  };
};
